import { useMemo } from "react";
import { Check, X } from "lucide-react";

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

interface StrengthResult {
  score: number;
  label: string;
  color: string;
  bgColor: string;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

export const PasswordStrengthIndicator = ({ 
  password, 
  showRequirements = true 
}: PasswordStrengthIndicatorProps) => {
  const strength = useMemo((): StrengthResult => {
    const checks = {
      length: password.length >= 10,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };

    const passedChecks = Object.values(checks).filter(Boolean).length;

    if (passedChecks === 0 || password.length === 0) {
      return {
        score: 0,
        label: "",
        color: "",
        bgColor: "",
        checks,
      };
    }

    if (passedChecks <= 2) {
      return {
        score: 1,
        label: "Schwach",
        color: "text-red-600",
        bgColor: "bg-red-600",
        checks,
      };
    }

    if (passedChecks <= 4) {
      return {
        score: 2,
        label: "Mittel",
        color: "text-yellow-600",
        bgColor: "bg-yellow-600",
        checks,
      };
    }

    return {
      score: 3,
      label: "Stark",
      color: "text-green-600",
      bgColor: "bg-green-600",
      checks,
    };
  }, [password]);

  if (password.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mt-2">
      {/* Strength Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Passwortstärke
          </span>
          {strength.label && (
            <span className={`text-xs font-semibold ${strength.color}`}>
              {strength.label}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                level <= strength.score
                  ? strength.bgColor
                  : "bg-muted"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Requirements Checklist */}
      {showRequirements && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Anforderungen:
          </p>
          <div className="grid gap-1">
            <RequirementItem
              met={strength.checks.length}
              text="Mindestens 10 Zeichen"
            />
            <RequirementItem
              met={strength.checks.uppercase}
              text="Ein Großbuchstabe (A-Z)"
            />
            <RequirementItem
              met={strength.checks.lowercase}
              text="Ein Kleinbuchstabe (a-z)"
            />
            <RequirementItem
              met={strength.checks.number}
              text="Eine Ziffer (0-9)"
            />
            <RequirementItem
              met={strength.checks.special}
              text="Ein Sonderzeichen (!@#$%...)"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
  <div className="flex items-center gap-2 text-xs">
    {met ? (
      <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
    ) : (
      <X className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
    )}
    <span className={met ? "text-foreground" : "text-muted-foreground"}>
      {text}
    </span>
  </div>
);
